import React, { useState, useEffect } from 'react';
import { auth, db, functions } from './firebase/config';
import { httpsCallable } from 'firebase/functions';
import {
  collection, doc, getDocs, getDoc, setDoc,
  serverTimestamp, writeBatch
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { roleLabel } from './i18n/el';

const u = {
  loading: 'Φόρτωση χρηστών…',
  title: 'Διαχείριση χρηστών',
  subtitle: 'Διαχειριστείτε τα μέλη της ομάδας και τα δικαιώματά τους',
  accessDenied: 'Χωρίς πρόσβαση',
  accessDeniedBody: 'Μόνο ο ιδιοκτήτης μπορεί να διαχειριστεί χρήστες.',
  backToDashboard: 'Επιστροφή στον πίνακα',
  addMemberTitle: 'Προσθήκη νέου μέλους',
  nameLabel: 'Όνομα (προαιρετικό)',
  namePlaceholder: 'Γιώργης Παπαδόπουλος',
  emailLabel: 'Email*',
  emailPlaceholder: 'member@example.com',
  roleLabel: 'Ρόλος*',
  roleManager: 'Διαχειριστής',
  roleAccountant: 'Λογιστής',
  addButton: 'Προσθήκη μέλους',
  requiredNote: '* Υποχρεωτικά πεδία',
  membersTitle: 'Μέλη ομάδας',
  noMembers: 'Δεν υπάρχουν μέλη ακόμα. Προσθέστε το πρώτο μέλος παραπάνω.',
  colUser: 'Χρήστης',
  colRole: 'Ρόλος',
  colStatus: 'Κατάσταση',
  colJoined: 'Εγγραφή',
  statusPending: 'Σε αναμονή',
  statusActive: 'Ενεργός',
  you: 'Εσείς',
  ownerLabel: 'Ιδιοκτήτης',
  resend: 'Επαναποστολή',
  resending: 'Αποστολή…',
  inviteEmailSent: 'Email στάλθηκε',
  inviteEmailPending: 'Αναμονή αποστολής…',
  inviteEmailFailed: 'Αποτυχία αποστολής email',
  remove: 'Αφαίρεση',
  pending: 'Σε αναμονή',
  permissionsTitle: 'Δικαιώματα ρόλων',
  permOwner: 'Πλήρης πρόσβαση σε όλες τις λειτουργίες, διαχείριση χρηστών και ρυθμίσεων.',
  permManager: 'Μπορεί να ανεβάζει, λαμβάνει και διαχειρίζεται τιμολόγια. Δεν μπορεί να αλλάξει ρυθμίσεις ή χρήστες.',
  permAccountant: 'Μόνο προβολή και λήψη τιμολογίων.',
  confirmRemoveTitle: 'Αφαίρεση μέλους',
  confirmRemoveBody: (email, isPending) =>
    `Είστε σίγουροι ότι θέλετε να αφαιρέσετε το ${email}?${isPending ? ' Αυτό θα ακυρώσει την πρόσκληση.' : ' Θα χάσει την πρόσβαση στην εταιρεία.'}`,
  cancel: 'Ακύρωση',
  confirmRemove: 'Αφαίρεση',
  toastInviteSent: (email) => `Πρόσκληση στάλθηκε στο ${email}`,
  toastResent: (email) => `Πρόσκληση επαναστάλθηκε στο ${email}`,
  toastRoleUpdated: 'Ο ρόλος ενημερώθηκε',
  toastRemoved: 'Το μέλος αφαιρέθηκε',
  toastAlreadyMember: 'Ο χρήστης είναι ήδη μέλος.',
  toastEmailRequired: 'Email και ρόλος είναι υποχρεωτικά.',
  toastInvalidEmail: 'Μη έγκυρο email.',
  toastCannotChangeOwn: 'Δεν μπορείτε να αλλάξετε τον δικό σας ρόλο.',
  toastCannotRemoveSelf: 'Δεν μπορείτε να αφαιρέσετε τον εαυτό σας.',
  toastLoadFailed: 'Αποτυχία φόρτωσης μελών.',
  toastInviteFailed: 'Αποτυχία αποστολής πρόσκλησης.',
  toastResendFailed: 'Αποτυχία επαναποστολής πρόσκλησης.',
  toastRoleFailed: 'Αποτυχία ενημέρωσης ρόλου.',
  toastRemoveFailed: 'Αποτυχία αφαίρεσης μέλους.',
  toastNoAccess: 'Δεν έχετε πρόσβαση σε εταιρεία.',
  toastMissingInfo: 'Λείπουν στοιχεία εταιρείας.',
};

export default function UserManagement() {
  const [members, setMembers] = useState([]);
  const [newMember, setNewMember] = useState({ email: '', role: 'manager', name: '' });
  const [userRole, setUserRole] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ visible: false, message: '', type: '' });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [resendingInvite, setResendingInvite] = useState(null);
  const [companyName, setCompanyName] = useState('');

  const navigate = useNavigate();
  const user = auth.currentUser;

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    const checkUserAccess = async () => {
      try {
        const companyDoc = await getDoc(doc(db, 'companies', user.uid));
        if (companyDoc.exists()) {
          setUserRole('owner');
          setCompanyId(user.uid);
          setCompanyName(companyDoc.data().companyName || '');
          loadMembers(user.uid);
        } else {
          const membershipDoc = await getDoc(doc(db, 'userMemberships', user.uid));
          if (membershipDoc.exists()) {
            const data = membershipDoc.data();
            setCompanyId(data.companyId);
            setUserRole(data.role);
            const companySnap = await getDoc(doc(db, 'companies', data.companyId));
            setCompanyName(companySnap.exists() ? companySnap.data().companyName || '' : '');
            if (data.role !== 'owner') { showToast(u.accessDeniedBody, 'error'); navigate('/'); return; }
            loadMembers(data.companyId);
          } else {
            showToast(u.toastNoAccess, 'error');
            navigate('/');
          }
        }
      } catch { showToast(u.toastLoadFailed, 'error'); }
    };
    checkUserAccess();
  }, [user, navigate]);

  const loadMembers = async (cId) => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'companies', cId, 'members'));
      const rows = await Promise.all(snap.docs.map(async (d) => {
        const data = { id: d.id, ...d.data() };
        if (data.status === 'pending' && data.invitationId) {
          try {
            const invSnap = await getDoc(doc(db, 'invitations', data.invitationId));
            if (invSnap.exists()) {
              data.inviteStatus = invSnap.data().status || 'pending';
              data.inviteError = invSnap.data().error || '';
            }
          } catch { /* ignore */ }
        }
        return data;
      }));
      setMembers(rows);
    } catch { showToast(u.toastLoadFailed, 'error'); }
    finally { setLoading(false); }
  };

  const resendInvitationClient = async (member) => {
    const newInvRef = doc(collection(db, 'invitations'));
    await setDoc(newInvRef, {
      email: member.email,
      role: member.role,
      companyId,
      companyName,
      invitedBy: user.uid,
      inviterName: user.displayName || user.email || '',
      createdAt: serverTimestamp(),
      status: 'pending',
    });
    await setDoc(
      doc(db, 'companies', companyId, 'members', member.id),
      { invitationId: newInvRef.id },
      { merge: true },
    );
  };

  const handleResendInvitation = async (member) => {
    setResendingInvite(member.id);
    try {
      try {
        const resendFn = httpsCallable(functions, 'resendInvitation');
        await resendFn({
          companyId,
          memberId: member.id,
          email: member.email,
          role: member.role,
        });
      } catch (callableErr) {
        // Callable may fail (IAM / not deployed). Client rules allow create on invitations.
        console.warn('resendInvitation callable failed, using client fallback:', callableErr?.code || callableErr);
        await resendInvitationClient(member);
      }
      showToast(u.toastResent(member.email), 'success');
      loadMembers(companyId);
    } catch (err) {
      console.error('Resend invitation failed:', err);
      showToast(u.toastResendFailed, 'error');
    } finally {
      setResendingInvite(null);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!newMember.email || !newMember.role) { showToast(u.toastEmailRequired, 'error'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newMember.email)) { showToast(u.toastInvalidEmail, 'error'); return; }
    setLoading(true);
    try {
      if (members.some(m => m.email.toLowerCase() === newMember.email.toLowerCase())) {
        showToast(u.toastAlreadyMember, 'error'); setLoading(false); return;
      }
      const invRef = doc(collection(db, 'invitations'));
      await setDoc(invRef, {
        email: newMember.email, role: newMember.role, companyId,
        companyName, invitedBy: user.uid, inviterName: user.displayName || '',
        name: newMember.name || '', createdAt: serverTimestamp(), status: 'pending'
      });
      const pendingId = `pending_${Date.now()}`;
      await setDoc(doc(db, 'companies', companyId, 'members', pendingId), {
        role: newMember.role, email: newMember.email, name: newMember.name || '',
        joinedAt: serverTimestamp(), invitedBy: user.uid, status: 'pending', invitationId: invRef.id
      });
      showToast(u.toastInviteSent(newMember.email), 'success');
      setNewMember({ email: '', role: 'manager', name: '' });
      loadMembers(companyId);
    } catch { showToast(u.toastInviteFailed, 'error'); }
    finally { setLoading(false); }
  };

  const handleRoleChange = async (memberId, newRole) => {
    if (memberId === user.uid) { showToast(u.toastCannotChangeOwn, 'error'); return; }
    try {
      setLoading(true);
      const member = members.find(m => m.id === memberId);
      await setDoc(doc(db, 'companies', companyId, 'members', memberId), { role: newRole }, { merge: true });
      try {
        const mDoc = await getDoc(doc(db, 'userMemberships', memberId));
        if (mDoc.exists()) await setDoc(doc(db, 'userMemberships', memberId), { role: newRole }, { merge: true });
      } catch { /* pending user */ }
      try {
        if (member?.status === 'pending' && member.invitationId)
          await setDoc(doc(db, 'invitations', member.invitationId), { role: newRole }, { merge: true });
      } catch { /* ok */ }
      showToast(u.toastRoleUpdated, 'success');
      loadMembers(companyId);
    } catch { showToast(u.toastRoleFailed, 'error'); }
    finally { setLoading(false); }
  };

  const handleDeleteMember = async () => {
    if (!confirmDelete) return;
    if (confirmDelete.id === user.uid) { showToast(u.toastCannotRemoveSelf, 'error'); setConfirmDelete(null); return; }
    try {
      setLoading(true);
      const batch = writeBatch(db);
      batch.delete(doc(db, 'companies', companyId, 'members', confirmDelete.id));
      try {
        const mDoc = await getDoc(doc(db, 'userMemberships', confirmDelete.id));
        if (mDoc.exists()) batch.delete(doc(db, 'userMemberships', confirmDelete.id));
      } catch { /* ok */ }
      await batch.commit();
      showToast(u.toastRemoved, 'success');
      loadMembers(companyId);
    } catch { showToast(u.toastRemoveFailed, 'error'); }
    finally { setLoading(false); setConfirmDelete(null); }
  };

  const showToast = (message, type = 'success') => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast({ visible: false, message: '', type: '' }), 3000);
  };

  if (loading && members.length === 0) {
    return (
      <div className="max-w-4xl mx-auto py-10">
        <div className="bg-white shadow rounded-lg p-8 flex items-center justify-center">
          <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span className="ml-2 text-gray-600">{u.loading}</span>
        </div>
      </div>
    );
  }

  if (userRole !== 'owner') {
    return (
      <div className="max-w-4xl mx-auto py-10">
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-red-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">{u.accessDenied}</h2>
          <p className="text-gray-600 mb-4">{u.accessDeniedBody}</p>
          <button onClick={() => navigate('/')} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">{u.backToDashboard}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-6">
      {toast.visible && (
        <div className={`fixed top-4 right-4 px-4 py-3 rounded shadow-lg z-50 text-white ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
          {toast.message}
        </div>
      )}

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-2">{u.confirmRemoveTitle}</h3>
            <p className="text-sm text-gray-500 mb-4">{u.confirmRemoveBody(confirmDelete.email, confirmDelete.status === 'pending')}</p>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">{u.cancel}</button>
              <button onClick={handleDeleteMember} className="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700">{u.confirmRemove}</button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="bg-blue-600 px-6 py-4">
          <h2 className="text-xl font-bold text-white">{u.title}</h2>
          <p className="text-blue-100 text-sm">{u.subtitle}</p>
        </div>
        <div className="p-6">
          {/* Add member form */}
          <div className="mb-8 bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">{u.addMemberTitle}</h3>
            <form onSubmit={handleAddMember} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">{u.nameLabel}</label>
                  <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2" value={newMember.name} onChange={(e) => setNewMember({ ...newMember, name: e.target.value })} placeholder={u.namePlaceholder} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{u.emailLabel}</label>
                  <input type="email" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2" value={newMember.email} onChange={(e) => setNewMember({ ...newMember, email: e.target.value })} placeholder={u.emailPlaceholder} required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{u.roleLabel}</label>
                  <select className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2" value={newMember.role} onChange={(e) => setNewMember({ ...newMember, role: e.target.value })} required>
                    <option value="manager">{u.roleManager}</option>
                    <option value="accountant">{u.roleAccountant}</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500">{u.requiredNote}</p>
                <button type="submit" disabled={loading} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
                  {loading && <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>}
                  {u.addButton}
                </button>
              </div>
            </form>
          </div>

          {/* Members list */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">{u.membersTitle}</h3>
            {members.length === 0 ? (
              <div className="bg-gray-50 p-6 text-center rounded-lg border border-gray-200">
                <p className="text-gray-500">{u.noMembers}</p>
              </div>
            ) : (
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {[u.colUser, u.colRole, u.colStatus, u.colJoined, ''].map((h, i) => (
                        <th key={i} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {members.map((member) => (
                      <tr key={member.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                              <span className="font-medium text-gray-600">{(member.name || member.email || '?').charAt(0).toUpperCase()}</span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{member.name || '—'}</div>
                              <div className="text-sm text-gray-500">{member.email}</div>
                            </div>
                            {member.id === user.uid && (
                              <span className="ml-2 px-2 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">{u.you}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {member.id === user.uid ? (
                            <span className="px-2 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">{u.ownerLabel}</span>
                          ) : (
                            <select value={member.role} onChange={(e) => handleRoleChange(member.id, e.target.value)} className="text-sm rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" disabled={loading}>
                              <option value="manager">{u.roleManager}</option>
                              <option value="accountant">{u.roleAccountant}</option>
                            </select>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col items-start space-y-1">
                            <span className={`px-2 text-xs font-semibold rounded-full ${member.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                              {member.status === 'pending' ? u.statusPending : u.statusActive}
                            </span>
                            {member.status === 'pending' && (
                              <button onClick={() => handleResendInvitation(member)} disabled={resendingInvite === member.id} className="ml-2 text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded hover:bg-blue-200 disabled:opacity-50">
                                {resendingInvite === member.id ? u.resending : u.resend}
                              </button>
                            )}
                            {member.status === 'pending' && member.inviteStatus === 'sent' && (
                              <span className="text-xs text-emerald-700">{u.inviteEmailSent}</span>
                            )}
                            {member.status === 'pending' && member.inviteStatus === 'pending' && (
                              <span className="text-xs text-gray-500">{u.inviteEmailPending}</span>
                            )}
                            {member.status === 'pending' && member.inviteStatus === 'error' && (
                              <span className="text-xs text-red-600" title={member.inviteError || ''}>{u.inviteEmailFailed}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {member.joinedAt?.seconds
                            ? new Date(member.joinedAt.seconds * 1000).toLocaleDateString('el-GR')
                            : member.joinedAt
                              ? new Date(member.joinedAt).toLocaleDateString('el-GR')
                              : u.pending}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {member.id !== user.uid && (
                            <button onClick={() => setConfirmDelete(member)} className="text-red-600 hover:text-red-900" disabled={loading}>{u.remove}</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {members.length > 0 && (
              <div className="mt-4 text-sm text-gray-500">
                <h4 className="font-medium text-gray-700 mb-1">{u.permissionsTitle}</h4>
                <ul className="list-disc pl-5 space-y-1">
                  <li><span className="font-medium">{u.ownerLabel}:</span> {u.permOwner}</li>
                  <li><span className="font-medium">{u.roleManager}:</span> {u.permManager}</li>
                  <li><span className="font-medium">{u.roleAccountant}:</span> {u.permAccountant}</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
